import { Alert, Button, Checkbox, Group, Stack, Text } from '@mantine/core';
import { useState } from 'react';
import { IconAlertTriangle } from '@tabler/icons-react';
import {
  useAutomationRetirementMutation,
  useAutomationRetirementPreflight,
} from '@/hooks/api/automations/useAutomationRetirement.ts';
import { Automation } from '@/types/automations.ts';

type Props = { automation: Automation; shouldLoad: boolean };

const AutomationRetirementPanel = ({ automation, shouldLoad }: Props) => {
  const [confirmed, setConfirmed] = useState(false);
  const retired = !!automation.retired_at;
  const { data: preflight, isLoading, isError: preflightError } = useAutomationRetirementPreflight(automation.id, shouldLoad);
  const { mutate: setRetired, isPending, error } = useAutomationRetirementMutation(automation.id);

  const impactText = preflight
    ? `${preflight.historical_dependency_count} historical caller${preflight.historical_dependency_count === 1 ? '' : 's'}, ` +
      `${preflight.active_parent_head_count} current parent head${preflight.active_parent_head_count === 1 ? '' : 's'}, ` +
      `${preflight.in_flight_run_count} in-flight run${preflight.in_flight_run_count === 1 ? '' : 's'}.`
    : '';

  return (
    <Stack gap="sm">
      <Alert icon={<IconAlertTriangle size={16} />} color={retired ? 'orange' : 'blue'}>
        {retired
          ? 'This automation is retired. Historical versions and runs remain available, but no new run or sub-automation call can start.'
          : 'Retirement preserves history while blocking future manual, portal, operational, and sub-automation runs.'}
      </Alert>
      {isLoading ? <Text size="sm">Loading retirement impact…</Text> : preflightError ? <Text size="sm" c="red">Retirement impact could not be loaded. Try again before changing this lifecycle state.</Text> : <Text size="sm">{impactText}</Text>}
      {!!preflight?.latest_parent_heads.length && (
        <Text size="sm" c="dimmed">
          Latest parent heads: {preflight.latest_parent_heads.map((head) => `${head.automation_name} v${head.version_number}`).join(', ')}.
        </Text>
      )}
      {!!preflight?.published_parent_heads.length && (
        <Text size="sm" c="dimmed">
          Published parent heads: {preflight.published_parent_heads.map((head) => `${head.automation_name} v${head.version_number}`).join(', ')}.
        </Text>
      )}
      {preflight?.public_portal_enabled && !retired && (
        <Text size="sm" c="dimmed">Retiring disables its public portal. Unretiring does not re-enable it.</Text>
      )}
      <Checkbox
        checked={confirmed}
        onChange={(event) => setConfirmed(event.currentTarget.checked)}
        label={retired ? 'I understand this restores future-run eligibility only.' : 'I understand retirement blocks future execution and preserves history.'}
      />
      {error && <Text size="sm" c="red">{error.response?.data?.detail ?? 'Retirement update failed.'}</Text>}
      <Group justify="flex-end">
        <Button
          color={retired ? 'green' : 'orange'}
          disabled={!confirmed || isLoading || !preflight}
          loading={isPending}
          onClick={() => setRetired({ retired: !retired }, { onSuccess: () => setConfirmed(false) })}
        >
          {retired ? 'Unretire automation' : 'Retire automation'}
        </Button>
      </Group>
    </Stack>
  );
};

export default AutomationRetirementPanel;
